import { useCallback } from "react"
import { useShallow } from "zustand/react/shallow"
import { useReplayStore, type ReplaySpeed } from "@/stores/replayStore"
import { useReplayResumeStore } from "@/stores/replayResumeStore"
import { useChartStore } from "@/stores/chartStore"
import { useDrawingStore } from "@/stores/drawingStore"
import { useReplayPreflight } from "@/hooks/useReplayPreflight"
import { forceChartJumpToLive } from "@/hooks/useViewportManager"

export function useReplayTransport() {
  const {
    sessionState,
    isActive,
    isReplayMode,
    position,
    anchorIndex,
    totalCandles,
    currentCandle,
    speed,
    startDate,
    errorMessage,
    loadProgress,
    togglePause,
    stepForward,
    stepBackward,
    setSpeed: storeSetSpeed,
  } = useReplayStore(
    useShallow((s) => ({
      sessionState: s.sessionState,
      isActive: s.isActive,
      isReplayMode: s.isReplayMode,
      position: s.position,
      anchorIndex: s.anchorIndex,
      totalCandles: s.totalCandles,
      currentCandle: s.currentCandle,
      speed: s.speed,
      startDate: s.startDate,
      errorMessage: s.errorMessage,
      loadProgress: s.loadProgress,
      togglePause: s.togglePause,
      stepForward: s.stepForward,
      stepBackward: s.stepBackward,
      setSpeed: s.setSpeed,
    })),
  )

  const { startPreflight, restartPreflight } = useReplayPreflight()

  const exit = useCallback(() => {
    const state = useReplayStore.getState()
    const resumeStore = useReplayResumeStore.getState()
    const chart = useChartStore.getState()

    const shouldCapture =
      state.isActive &&
      state.sessionState !== "ended" &&
      state.sessionState !== "loading" &&
      state.sessionState !== "error" &&
      state.startDate != null

    if (shouldCapture) {
      resumeStore.capture({
        symbol: chart.symbol,
        symbolDisplayName: chart.activeSymbolName,
        entryTimeframe: resumeStore.entryTimeframe ?? chart.timeframe,
        startDate: state.startDate!,
        exitTimestamp: state.currentCandle?.timestamp ?? Date.now(),
        speed: state.speed,
        contextCandleCount: state.contextCandleCount,
        candleCount: state.position - state.anchorIndex,
        lineColor: useDrawingStore.getState().drawingColor,
        capturedAt: Date.now(),
      })
    }

    state.exitReplayMode()

    if (state.isActive) {
      useReplayStore.getState().jumpToLive()
      forceChartJumpToLive()
    }

    const entryTf = useReplayResumeStore.getState().entryTimeframe
    const tf = useChartStore.getState().timeframe
    if (entryTf && tf !== entryTf) {
      try {
        useChartStore.getState().setTimeframe(entryTf)
      } catch (e) {
        console.warn("Failed to restore entry timeframe:", e)
      }
    }

    if (useReplayResumeStore.getState().snapshot) {
      useReplayResumeStore.getState().show()
    }
  }, [])

  const beginFromDate = useCallback(
    (timestamp: number) => {
      useReplayStore.getState().enterReplayMode()
      void startPreflight(timestamp, Date.now())
    },
    [startPreflight],
  )

  const setSpeed = useCallback(
    (s: ReplaySpeed) => storeSetSpeed(s),
    [storeSetSpeed],
  )

  return {
    sessionState,
    isActive,
    isReplayMode,
    position,
    anchorIndex,
    totalCandles,
    currentCandle,
    speed,
    startDate,
    errorMessage,
    loadProgress,
    exit,
    togglePlayPause: togglePause,
    stepBack: stepBackward,
    stepForward,
    setSpeed,
    beginFromDate,
    restartPreflight,
  }
}
